import { useCallback, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useUnsavedFormGuard } from "@/hooks/useUnsavedFormGuard";
import { useInvalidAddressSaveConfirm } from "@/hooks/useInvalidAddressSaveConfirm";
import { toastApiError } from "@/lib/errorMessages";
import { isLocationDirty } from "@/lib/locationForm";
import { houseService } from "@/services/houseService";
import type {
  AddressValidationState,
  House,
  HouseWithDeviceCount,
} from "@/types";
import {
  buildHouseAdminFormDefaults,
  buildHouseAdminFormSchema,
  buildHouseHostFormDefaults,
  buildHouseHostFormSchema,
  houseUpdateFromAdminForm,
  houseUpdateFromHostForm,
  needsUnlocatedAddressConfirm,
  type HouseAdminDetailFormValues,
  type HouseHostDetailFormValues,
} from "../houseDetailForm";

export function useHouseHostProfileForm(house: House, houseId: string) {
  const { t } = useTranslation("houses");
  const { t: tVal } = useTranslation("validation");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const formId = useId();

  const schema = useMemo(
    () =>
      buildHouseHostFormSchema({
        nameRequired: tVal("fieldRequired", {
          field: t("detail.modalHouseName"),
        }),
      }),
    [t, tVal],
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<HouseHostDetailFormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildHouseHostFormDefaults(house),
  });

  const [addressValidation, setAddressValidation] =
    useState<AddressValidationState>({ status: "idle" });
  const { saveBarPhase, notifySaved } = useUnsavedFormGuard(isDirty);
  const { requestInvalidAddressSaveConfirm, confirmModal } =
    useInvalidAddressSaveConfirm();

  const handleRevert = useCallback(() => {
    const baseline =
      queryClient.getQueryData<House>(["house", houseId]) ?? house;
    reset(buildHouseHostFormDefaults(baseline));
    setAddressValidation({ status: "idle" });
  }, [house, houseId, queryClient, reset]);

  const updateMutation = useMutation({
    mutationFn: (values: HouseHostDetailFormValues) =>
      houseService.update(houseId, houseUpdateFromHostForm(values)),
    onSuccess: (updated) => {
      queryClient.setQueryData(["house", houseId], updated);
      reset(buildHouseHostFormDefaults(updated));
      void queryClient.invalidateQueries({ queryKey: ["houses"] });
      toast.success(t("updated"));
      notifySaved();
    },
    onError: (err) => {
      toastApiError(err, "updateHouse");
    },
  });

  const onSubmit = (values: HouseHostDetailFormValues) => {
    if (needsUnlocatedAddressConfirm(values)) {
      requestInvalidAddressSaveConfirm(
        true,
        addressValidation,
        tCommon("addressValidation.saveWarningHouse"),
        () => updateMutation.mutate(values),
      );
      return;
    }
    updateMutation.mutate(values);
  };

  return {
    formId,
    register,
    handleSubmit,
    setValue,
    watch,
    errors,
    nameModified: !!dirtyFields.name,
    locationModified: isLocationDirty(dirtyFields),
    setAddressValidation,
    onSubmit,
    handleRevert,
    saveBarPhase,
    confirmModal,
    isPending: updateMutation.isPending,
  };
}

export function useHouseAdminProfileForm(
  house: HouseWithDeviceCount,
  houseId: string,
) {
  const { t } = useTranslation("houses");
  const { t: tVal } = useTranslation("validation");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const formId = useId();

  const schema = useMemo(
    () =>
      buildHouseAdminFormSchema({
        nameRequired: tVal("fieldRequired", {
          field: t("adminDetail.houseName"),
        }),
        commissionRequired: tVal("fieldRequired", {
          field: t("adminDetail.commissionPct"),
        }),
        commissionRange: t("createModal.commissionPercentRange"),
      }),
    [t, tVal],
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<HouseAdminDetailFormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildHouseAdminFormDefaults(house),
  });

  const [addressValidation, setAddressValidation] =
    useState<AddressValidationState>({ status: "idle" });
  const { saveBarPhase, notifySaved } = useUnsavedFormGuard(isDirty);
  const { requestInvalidAddressSaveConfirm, confirmModal } =
    useInvalidAddressSaveConfirm();

  const handleRevert = useCallback(() => {
    const baseline =
      queryClient.getQueryData<HouseWithDeviceCount>(["house", houseId]) ??
      house;
    reset(buildHouseAdminFormDefaults(baseline));
    setAddressValidation({ status: "idle" });
  }, [house, houseId, queryClient, reset]);

  const updateMutation = useMutation({
    mutationFn: (values: HouseAdminDetailFormValues) =>
      houseService.adminUpdate(houseId, houseUpdateFromAdminForm(values)),
    onSuccess: (updated) => {
      queryClient.setQueryData<HouseWithDeviceCount>(
        ["house", houseId],
        (prev) => (prev ? { ...prev, ...updated } : undefined),
      );
      reset(buildHouseAdminFormDefaults(updated));
      void queryClient.invalidateQueries({ queryKey: ["house", houseId] });
      toast.success(t("adminDetail.toastUpdateSuccess"));
      notifySaved();
    },
    onError: (err) => {
      toastApiError(err, "updateHouse");
    },
  });

  const onSubmit = (values: HouseAdminDetailFormValues) => {
    if (needsUnlocatedAddressConfirm(values)) {
      requestInvalidAddressSaveConfirm(
        true,
        addressValidation,
        tCommon("addressValidation.saveWarningHouse"),
        () => updateMutation.mutate(values),
      );
      return;
    }
    updateMutation.mutate(values);
  };

  return {
    formId,
    register,
    handleSubmit,
    setValue,
    watch,
    errors,
    nameModified: !!dirtyFields.name,
    locationModified: isLocationDirty(dirtyFields),
    visibilityModified: !!dirtyFields.is_active,
    commissionModified: !!dirtyFields.commission_percent,
    listed: watch("is_active"),
    setAddressValidation,
    onSubmit,
    handleRevert,
    saveBarPhase,
    confirmModal,
    isPending: updateMutation.isPending,
  };
}
